import React, { useState } from 'react';
import { C } from '../styles';
import { useAuth } from '../AuthContext';

const ALL_NAV_ITEMS = [
  { label: 'Dashboard', page: 'dashboard' },
  { label: 'Projects', page: 'projects' },
  { label: 'Instructions', page: 'instructions' },
  { label: 'Dayworks', page: 'dayworks' },
  { label: 'Variations', page: 'variations' },
  { label: 'Admin', page: 'admin' },
];

const NAV_PAGES_BY_ROLE = {
  admin: ['dashboard', 'projects', 'instructions', 'dayworks', 'variations', 'admin'],
  proline_qs: ['dashboard', 'projects', 'variations'],
  proline_sm: [],
};

const ROLE_LABELS = {
  admin: 'Administrator',
  proline_sm: 'Site Manager',
  proline_qs: 'Quantity Surveyor',
  mc_qs: 'MC Quantity Surveyor',
};

export default function Layout({ children, navigate, currentPage }) {
  const { profile, signOut } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const role = profile?.role || '';
  const allowedPages = NAV_PAGES_BY_ROLE[role] || [];
  const visibleItems = ALL_NAV_ITEMS.filter(item => allowedPages.includes(item.page));
  const roleLabel = ROLE_LABELS[role] || role;

  function handleNav(page) {
    navigate(page, {});
    setMobileMenuOpen(false);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: C.bg }}>
      {/* Top navigation header */}
      <header style={{
        background: '#000000',
        height: '56px',
        display: 'flex',
        alignItems: 'center',
        padding: '0 24px',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
      }}>
        {/* Left: Brand */}
        <div
          onClick={() => navigate('dashboard', {})}
          style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: '180px', cursor: 'pointer' }}
        >
          <div style={{ fontSize: '16px', fontWeight: '700', color: '#ffffff', lineHeight: 1.2 }}>PROLINE INTERNALS LTD</div>
          <div style={{ fontSize: '11px', color: '#999999', letterSpacing: '0.08em', textTransform: 'uppercase', lineHeight: 1.2 }}>COMMERCIAL MANAGEMENT</div>
        </div>

        {/* Center: Nav links (desktop) */}
        <nav style={{ flex: 1, display: 'flex', justifyContent: 'center', gap: '32px' }} className="nav-center">
          {visibleItems.map(item => {
            const isActive = currentPage === item.page;
            return (
              <button
                key={item.page}
                onClick={() => handleNav(item.page)}
                style={{
                  background: 'none',
                  border: 'none',
                  borderBottom: isActive ? '2px solid #ffffff' : '2px solid transparent',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: isActive ? '600' : '400',
                  opacity: isActive ? 1 : 0.8,
                  cursor: 'pointer',
                  padding: '0 0 2px 0',
                  height: '56px',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'opacity 0.15s',
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.opacity = '1'; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.opacity = '0.8'; }}
              >
                {item.label}
              </button>
            );
          })}
        </nav>

        {/* Hamburger (mobile only) */}
        <button
          onClick={() => setMobileMenuOpen(o => !o)}
          className="hamburger-btn"
          style={{
            display: 'none',
            flex: 1,
            justifyContent: 'center',
            background: 'none',
            border: 'none',
            color: '#ffffff',
            fontSize: '24px',
            cursor: 'pointer',
            padding: '0',
          }}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? '✕' : '☰'}
        </button>

        {/* Right: User info + Sign Out */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', minWidth: '180px', justifyContent: 'flex-end' }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '14px', fontWeight: '700', color: '#ffffff', lineHeight: 1.2 }}>
              {profile?.full_name || ''}
            </div>
            <div style={{ fontSize: '12px', color: '#999999', lineHeight: 1.2 }}>{roleLabel}</div>
          </div>
          <button
            onClick={signOut}
            style={{
              background: 'transparent',
              color: '#ffffff',
              border: '1px solid rgba(255,255,255,0.5)',
              borderRadius: '4px',
              padding: '6px 14px',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'background 0.15s, color 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.color = '#000000'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#ffffff'; }}
          >
            Sign Out
          </button>
        </div>
      </header>

      {/* Mobile dropdown menu */}
      {mobileMenuOpen && (
        <div
          className="mobile-menu"
          style={{
            display: 'none',
            position: 'fixed',
            top: '56px',
            left: 0,
            right: 0,
            background: '#000000',
            zIndex: 99,
            borderTop: '1px solid #222222',
          }}
        >
          {visibleItems.map(item => {
            const isActive = currentPage === item.page;
            return (
              <button
                key={item.page}
                onClick={() => handleNav(item.page)}
                style={{
                  display: 'block',
                  width: '100%',
                  height: '48px',
                  background: isActive ? 'rgba(255,255,255,0.08)' : 'transparent',
                  border: 'none',
                  borderBottom: '1px solid #222222',
                  color: '#ffffff',
                  fontSize: '15px',
                  fontWeight: isActive ? '600' : '400',
                  textAlign: 'left',
                  padding: '0 24px',
                  cursor: 'pointer',
                }}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Main content */}
      <main style={{ flex: 1, paddingTop: '56px', minWidth: 0 }}>
        {children}
      </main>

      {/* Inline responsive styles */}
      <style>{`
        @media (max-width: 768px) {
          .nav-center { display: none !important; }
          .hamburger-btn { display: flex !important; }
          .mobile-menu { display: block !important; }
        }
      `}</style>
    </div>
  );
}
